import { COMPARE_AT_PROPERTY, formatMoney } from "@theme/cart-line-item-properties";

/**
 * cart-savings.js — strike-through pricing and the savings breakdown in the cart.
 *
 * Shopify's compare-at price is a display field: it never reaches the cart object,
 * so the theme stamps it on each line as the hidden `_compare_at_price` property at
 * add-to-cart (see `cart-line-item-properties.js`). Liquid then exposes that value,
 * the line quantity and Shopify's own line totals as data attributes, and this module
 * does every piece of arithmetic in integer cents:
 *
 *  - per line:   original = max(original_line_price, compare_at × quantity)
 *                markdown savings = compare_at × quantity − original_line_price
 *  - cart:       markdown savings = Σ line markdown savings
 *                tier / other discounts = Shopify's discount allocations, bucketed by
 *                title (see `bucketDiscounts`) — never recomputed from thresholds
 *                total savings = markdown savings + cart.total_discount
 *                strike total  = cart.original_total_price + markdown savings
 *
 * The DOM is only touched where the compare-at price changes what Liquid rendered, so a
 * cart with no marked-down lines renders exactly as before this module existed.
 *
 * Re-runs after every section morph (`section:morph`) and cart update (`cart:update`):
 * quantity changes, GWP adds/removes, discount codes and bundle edits all flow through
 * one of those two events.
 */

const OPEN_STORAGE_KEY = "bww_cart_savings_open";

/** @param {unknown} value */
function toInt(value) {
  const n = parseInt(String(value ?? ""), 10);
  return Number.isFinite(n) ? n : 0;
}

/** @param {string|undefined} csv */
function intList(csv) {
  if (!csv) return [];
  return csv.split(",").map(part => toInt(part.trim()));
}

/**
 * Splits a formatted money string into the integer part and the cents shown as a
 * superscript, mirroring `snippets/cart-price.liquid` (`split: '.'`).
 * @param {number} cents
 * @param {boolean} withCurrency
 */
function moneyParts(cents, withCurrency) {
  const theme = /** @type {any} */ (window).Theme || {};
  const format = withCurrency ? theme.moneyWithCurrencyFormat || theme.moneyFormat : theme.moneyFormat;
  const formatted = formatMoney(cents, format);
  const usesCommaDecimal = typeof format === "string" && format.includes("_with_comma_separator");
  const separator = usesCommaDecimal ? "," : ".";
  const idx = formatted.lastIndexOf(separator);
  if (idx === -1) return { formatted, integer: formatted, decimal: "" };
  // Anything after the separator that is not a digit (e.g. " USD") belongs to the integer side.
  const tail = formatted.slice(idx + 1);
  const digits = tail.match(/^\d+/)?.[0] ?? "";
  const suffix = tail.slice(digits.length);
  return { formatted, integer: formatted.slice(0, idx) + suffix, decimal: digits };
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/**
 * Renders one `<text-component>` price the same way `cart-price.liquid` does.
 * @param {number} cents
 * @param {{ className: string, withCurrency: boolean, productId?: string, attrs?: string, ref?: string }} opts
 */
function priceHtml(cents, opts) {
  const { formatted, integer, decimal } = moneyParts(cents, opts.withCurrency);
  const sup = decimal && decimal !== "00" ? `<sup>${decimal}</sup>` : "";
  const productId = opts.productId ? ` data-product-id="${escapeHtml(opts.productId)}"` : "";
  const ref = opts.ref ? ` ref="${opts.ref}"` : "";
  return (
    `<text-component${ref} value="${escapeHtml(formatted)}" class="${opts.className}"${productId}${opts.attrs ? ` ${opts.attrs}` : ""}>` +
    `${escapeHtml(integer)}${sup}</text-component>`
  );
}

/**
 * One cart row's pricing inputs, read from the attributes Liquid stamped on the `<tr>`.
 * Regular rows carry a single line; bundle rows carry one entry per bundled line.
 *
 * @typedef {object} RowPricing
 * @property {HTMLElement} row
 * @property {HTMLElement} priceHost - the `[data-cart-line-price]` wrapper
 * @property {boolean} bundle
 * @property {number} finalLinePrice
 * @property {number} originalLinePrice - Shopify's pre-discount line total (or the
 *   subscription compare-at total Liquid already substitutes for it)
 * @property {number} compareAtLinePrice - Σ compare_at × quantity
 */

/**
 * @param {HTMLElement} priceHost
 * @returns {RowPricing|null}
 */
function readRow(priceHost) {
  const row = /** @type {HTMLElement|null} */ (priceHost.closest("tr"));
  if (!row) return null;
  const d = row.dataset;

  if (d.bundleQuantities !== undefined) {
    const compareAts = intList(d.bundleCompareAtPrices);
    const quantities = intList(d.bundleQuantities);
    const finals = intList(d.bundleFinalLinePrices);
    const originals = intList(d.bundleOriginalLinePrices);
    let compareAtLinePrice = 0;
    for (let i = 0; i < quantities.length; i++) {
      const compareAt = compareAts[i] || 0;
      const qty = quantities[i] || 0;
      const unitOriginal = qty > 0 ? Math.round((originals[i] || 0) / qty) : 0;
      // A compare-at at or below the selling price is not a markdown — ignore it.
      compareAtLinePrice += compareAt > unitOriginal ? compareAt * qty : originals[i] || 0;
    }
    return {
      row,
      priceHost,
      bundle: true,
      finalLinePrice: finals.reduce((sum, n) => sum + n, 0),
      originalLinePrice: originals.reduce((sum, n) => sum + n, 0),
      compareAtLinePrice,
    };
  }

  if (d.isGwp === "true") return null;

  const quantity = toInt(d.quantity);
  const compareAt = toInt(d.compareAtPrice);
  const originalLinePrice = toInt(d.originalLinePrice);
  const unitOriginal = quantity > 0 ? Math.round(originalLinePrice / quantity) : 0;
  return {
    row,
    priceHost,
    bundle: false,
    finalLinePrice: toInt(d.finalLinePrice),
    originalLinePrice,
    compareAtLinePrice: compareAt > unitOriginal && quantity > 0 ? compareAt * quantity : originalLinePrice,
  };
}

/**
 * @param {RowPricing} line
 * @returns {{ displayOriginal: number, markdownSavings: number }}
 */
export function computeLine(line) {
  const displayOriginal = Math.max(line.originalLinePrice, line.compareAtLinePrice);
  const markdownSavings = Math.max(0, line.compareAtLinePrice - line.originalLinePrice);
  return { displayOriginal, markdownSavings };
}

/**
 * @typedef {object} DiscountEntry
 * @property {string} title
 * @property {number} amount - cents
 * @property {boolean} [gwp_line] - allocation sits on a free-gift line
 * @property {string} [type] - Shopify discount application type ("discount_code", "automatic", "")
 */

/** @param {string|undefined} csv */
function keywordList(csv) {
  return String(csv ?? "")
    .split(",")
    .map(k => k.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Groups Shopify's discount allocations into the two named rows and "everything else".
 *
 * The tier / GWP discount is applied by a Shopify Function, so the only truthful amount
 * is the one Shopify actually allocated — recomputing "$25 off at $200" from the
 * progress-bar thresholds would drift the moment the function's rules and the theme's
 * settings disagreed. So discounts are never recomputed here, only sorted by title.
 *
 * Precedence for each allocation:
 *  1. on a free-gift line                          → tier row (always)
 *  2. a discount code, when `codesInMarkdown` is on → markdown ("Site-Wide Sale") row
 *  3. title matches `markdownMatch`                → markdown row
 *  4. title matches `tierMatch`                    → tier row
 *  5. otherwise                                    → its own row, keyed by title
 *
 * The markdown list is empty by default and only holds explicit merchant choices, while the
 * tier list ships with broad defaults ("gift", "gwp", "tier"), so an explicit choice wins.
 *
 * @param {DiscountEntry[]} discounts
 * @param {{ tierMatch: string[], markdownMatch?: string[], codesInMarkdown?: boolean, totalDiscount: number, otherLabel: string }} cfg
 * @returns {{ tier: number, markdown: number, others: Array<{ title: string, amount: number }> }}
 */
export function bucketDiscounts(discounts, cfg) {
  let tier = 0;
  let markdown = 0;
  /** @type {Map<string, number>} */
  const others = new Map();
  const tierKeywords = cfg.tierMatch.map(k => k.toLowerCase()).filter(Boolean);
  const markdownKeywords = (cfg.markdownMatch || []).map(k => k.toLowerCase()).filter(Boolean);

  for (const entry of discounts) {
    const amount = toInt(entry.amount);
    if (amount <= 0) continue;
    const title = String(entry.title || "").trim();
    const lower = title.toLowerCase();
    if (entry.gwp_line) {
      tier += amount;
    } else if (cfg.codesInMarkdown && entry.type === "discount_code") {
      markdown += amount;
    } else if (markdownKeywords.some(k => lower.includes(k))) {
      markdown += amount;
    } else if (tierKeywords.some(k => lower.includes(k))) {
      tier += amount;
    } else {
      const key = title || cfg.otherLabel;
      others.set(key, (others.get(key) || 0) + amount);
    }
  }

  // Shopify's total is the source of truth; anything not covered by an allocation we
  // saw (rounding, an allocation type Liquid does not expose) is still shown, not lost.
  const seen = tier + markdown + Array.from(others.values()).reduce((sum, n) => sum + n, 0);
  const remainder = cfg.totalDiscount - seen;
  if (remainder > 0) others.set(cfg.otherLabel, (others.get(cfg.otherLabel) || 0) + remainder);

  return { tier, markdown, others: Array.from(others, ([title, amount]) => ({ title, amount })) };
}

/**
 * Everything the summary needs, computed from one `cart-items-component` root.
 * @param {HTMLElement} root
 */
export function compute(root) {
  const summary = /** @type {HTMLElement|null} */ (root.querySelector("[data-cart-savings]"));
  /** @type {any} */
  let data = {};
  const script = root.querySelector("script[data-cart-savings-data]");
  if (script?.textContent) {
    try {
      data = JSON.parse(script.textContent);
    } catch (error) {
      console.warn("[CartSavings] Could not parse cart savings data", error);
    }
  }

  // `settings.cart_savings_markdown_enabled` off: ignore compare-at data entirely so the
  // cart shows only Shopify's discounts. The property is still stamped on add-to-cart, so
  // switching it back on applies to lines already in the cart.
  const markdownEnabled = summary?.dataset.markdownEnabled !== "false";

  const lines = [];
  let markdownSavings = 0;
  for (const host of root.querySelectorAll("[data-cart-line-price]")) {
    const line = readRow(/** @type {HTMLElement} */ (host));
    if (!line) continue;
    const result = markdownEnabled
      ? computeLine(line)
      : { displayOriginal: line.originalLinePrice, markdownSavings: 0 };
    markdownSavings += result.markdownSavings;
    lines.push({ line, ...result });
  }

  const totalDiscount = toInt(data.total_discount);
  const cfg = {
    tierMatch: keywordList(summary?.dataset.tierMatch),
    markdownMatch: keywordList(summary?.dataset.markdownMatch),
    codesInMarkdown: summary?.dataset.codesInMarkdown === "true",
    totalDiscount,
    otherLabel: summary?.dataset.labelOther || "Other discounts",
  };
  const buckets = bucketDiscounts(Array.isArray(data.discounts) ? data.discounts : [], cfg);

  return {
    summary,
    lines,
    // Compare-at savings only: this is what raises the strike-through prices. Shopify
    // discounts matched into the markdown row are already inside `total_discount`.
    markdownSavings,
    // What the "Site-Wide Sale" row shows: compare-at savings + matched Shopify discounts.
    markdownRowTotal: markdownSavings + buckets.markdown,
    tierSavings: buckets.tier,
    otherDiscounts: buckets.others,
    totalDiscount,
    totalSavings: markdownSavings + totalDiscount,
    totalPrice: toInt(data.total_price),
    displayOriginalTotal: toInt(data.original_total_price) + markdownSavings,
  };
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/**
 * @param {RowPricing} line
 * @param {number} displayOriginal
 */
function renderLinePrice(line, displayOriginal) {
  const host = line.priceHost;
  const withCurrency = host.dataset.currencyCode === "true";
  const productId = line.row.dataset.productId;

  if (line.bundle) {
    host.innerHTML =
      `<div class="flex flex-row items-center gap-1">` +
      priceHtml(line.finalLinePrice, { className: "text-16-bold text-brunt-orange igPrice", withCurrency }) +
      priceHtml(displayOriginal, {
        className: "text-12-regular line-through text-asphalt igComparePrice",
        withCurrency,
      }) +
      `</div>`;
    return;
  }

  host.innerHTML =
    `<div class="flex flex-col items-end"><div class="flex flex-row items-center">` +
    priceHtml(line.finalLinePrice, {
      className: "text-16-bold text-[#c74f1f] pr-1 igPrice",
      withCurrency,
      productId,
    }) +
    priceHtml(displayOriginal, {
      className: "text-12-regular line-through text-asphalt igComparePrice",
      withCurrency,
      productId,
    }) +
    `</div></div>`;
}

/**
 * @param {HTMLElement} host
 * @param {number} totalPrice
 * @param {number} displayOriginalTotal
 */
function renderTotal(host, totalPrice, displayOriginalTotal) {
  const withCurrency = host.dataset.currencyCode === "true";
  host.innerHTML =
    `<div class="flex flex-col items-end"><div class="flex flex-row items-center">` +
    priceHtml(totalPrice, {
      className: "text-16-bold text-[#c74f1f] pr-1 cart__total-value",
      withCurrency,
      ref: "cartTotal",
      attrs: "data-cart-subtotal",
    }) +
    priceHtml(displayOriginalTotal, { className: "text-12-regular line-through text-asphalt", withCurrency }) +
    `</div></div>`;
}

function readOpenState() {
  try {
    const stored = window.sessionStorage?.getItem(OPEN_STORAGE_KEY);
    return stored === null ? null : stored === "1";
  } catch {
    return null;
  }
}

/** @param {boolean} open */
function writeOpenState(open) {
  try {
    window.sessionStorage?.setItem(OPEN_STORAGE_KEY, open ? "1" : "0");
  } catch {
    // sessionStorage unavailable — state simply resets next morph
  }
}

/**
 * Re-applies the shopper's open/closed choice. Every morph re-renders the accordion
 * collapsed (Liquid cannot know client state), and re-collapsing on each quantity
 * change would make the breakdown unreadable while editing the cart.
 * @param {HTMLElement} summary
 */
function applyOpenState(summary) {
  const trigger = /** @type {HTMLElement|null} */ (summary.querySelector("[data-cart-savings-trigger]"));
  const content = /** @type {HTMLElement|null} */ (summary.querySelector("[data-cart-savings-content]"));
  if (!trigger || !content) return;
  const stored = readOpenState();
  const open = stored === null ? summary.dataset.openDefault === "true" : stored;
  trigger.setAttribute("aria-expanded", open ? "true" : "false");
  const label = open ? trigger.dataset.disclosureClose : trigger.dataset.disclosureOpen;
  if (label) trigger.setAttribute("aria-label", label);
  content.inert = !open;
}

/**
 * @param {ReturnType<typeof compute>} result
 * @param {HTMLElement} root
 */
function renderSummary(result, root) {
  const { summary } = result;
  if (!summary) return;

  if (result.totalSavings <= 0) {
    summary.hidden = true;
    return;
  }
  summary.hidden = false;

  const withCurrency = summary.dataset.currencyCode === "true";
  const totalEl = summary.querySelector("[data-cart-savings-total]");
  if (totalEl) {
    const { formatted } = moneyParts(result.totalSavings, withCurrency);
    totalEl.textContent = `-${formatted}`;
  }

  const list = summary.querySelector("[data-cart-savings-list]");
  if (list) {
    /** @type {Array<{ title: string, amount: number }>} */
    const rows = [];
    if (result.markdownRowTotal > 0) {
      rows.push({ title: summary.dataset.labelMarkdown || "Site-Wide Sale", amount: result.markdownRowTotal });
    }
    if (result.tierSavings > 0) {
      rows.push({ title: summary.dataset.labelTier || "Tiered Discount", amount: result.tierSavings });
    }
    rows.push(...result.otherDiscounts);

    list.innerHTML = rows
      .map(({ title, amount }) => {
        const { formatted } = moneyParts(amount, withCurrency);
        return (
          `<li class="cart-savings__row flex justify-between items-baseline gap-2 text-12-light">` +
          `<span class="cart-savings__row-label">${escapeHtml(title)}</span>` +
          `<span class="cart-savings__row-value">-${escapeHtml(formatted)}</span></li>`
        );
      })
      .join("");
  }

  applyOpenState(summary);

  // Estimated total: only re-render when the compare-at price raises the strike-through.
  const totalHost = /** @type {HTMLElement|null} */ (root.querySelector("[data-cart-total-price]"));
  if (totalHost && result.markdownSavings > 0 && result.displayOriginalTotal > result.totalPrice) {
    renderTotal(totalHost, result.totalPrice, result.displayOriginalTotal);
  }
}

/** @param {HTMLElement} root */
export function render(root) {
  const result = compute(root);
  for (const { line, displayOriginal, markdownSavings } of result.lines) {
    // Leave Liquid's output alone unless the compare-at price adds information.
    if (markdownSavings > 0 && displayOriginal > line.finalLinePrice) renderLinePrice(line, displayOriginal);
  }
  renderSummary(result, root);
  return result;
}

export function renderAll() {
  document.querySelectorAll("cart-items-component").forEach(root => {
    try {
      render(/** @type {HTMLElement} */ (root));
    } catch (error) {
      console.error("[CartSavings] render failed", error);
    }
  });
}

// ---------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------

/** Remember the shopper's choice when they toggle the breakdown. */
document.addEventListener(
  "click",
  event => {
    const target = /** @type {HTMLElement|null} */ (event.target instanceof Element ? event.target : null);
    const trigger = target?.closest("[data-cart-savings-trigger]");
    if (!trigger) return;
    // `disclosure-custom` flips aria-expanded in its own (capture-phase) handler first.
    queueMicrotask(() => writeOpenState(trigger.getAttribute("aria-expanded") === "true"));
  },
  { capture: false }
);

// `section:morph` fires synchronously after the DOM swap, so rendering here happens in the
// same task as the morph — the compare-at strike never flashes Liquid's plain price first.
document.addEventListener("section:morph", renderAll);
document.addEventListener("cart:update", () => requestAnimationFrame(renderAll));

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", renderAll);
} else {
  renderAll();
}

/** @type {any} */ (window).CartSavings = {
  compute,
  render,
  renderAll,
  bucketDiscounts,
  computeLine,
  COMPARE_AT_PROPERTY,
};
